// src/components/script-analysis/ShootabilityResult.jsx
import React from "react";

const LevelBadge = ({ level }) => {
  const map = {
    "低": { bg: "rgba(61,158,90,0.15)", color: "var(--color-success)" },
    "中": { bg: "rgba(212,167,68,0.15)", color: "var(--color-warning)" },
    "高": { bg: "rgba(217,58,58,0.15)", color: "var(--color-error)" },
  };
  const s = map[level] || map["中"];
  return <span className="text-2xs px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: s.bg, color: s.color }}>{level}</span>;
};

const ShootabilityBadge = ({ value }) => {
  const map = {
    "非常适合": { bg: "rgba(61,158,90,0.15)", color: "var(--color-success)" },
    "适合": { bg: "rgba(61,158,90,0.15)", color: "var(--color-success)" },
    "需要调整": { bg: "rgba(212,167,68,0.15)", color: "var(--color-warning)" },
    "不建议": { bg: "rgba(217,58,58,0.15)", color: "var(--color-error)" },
  };
  const s = map[value] || map["需要调整"];
  return <span className="text-xs px-2 py-1 rounded-lg font-semibold" style={{ backgroundColor: s.bg, color: s.color }}>{value}</span>;
};

const InfoRow = ({ label, value, badge }) => (
  <div className="mb-3">
    <div className="flex items-center gap-2 mb-1">
      <span className="text-xs font-medium" style={{ color: "var(--color-text-secondary)" }}>{label}</span>
      {badge}
    </div>
    <p className="text-2xs leading-relaxed" style={{ color: "var(--color-text-muted)" }}>{value}</p>
  </div>
);

const Section = ({ title, children }) => (
  <div className="rounded-xl p-5 border mb-4" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
    <h3 className="text-sm font-bold mb-3 pb-2 border-b" style={{ color: "var(--color-text-primary)", borderColor: "var(--color-border-primary)" }}>{title}</h3>
    {children}
  </div>
);

const ShootabilityResult = ({ data, theme }) => {
  if (!data) return null;
  const overall = data.overall || {};

  return (
    <div className="space-y-4">
      {/* Overall */}
      <div className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>适拍性结论</span>
          <ShootabilityBadge value={overall.shootability} />
        </div>
        {overall.bestApproach && (
          <div className="px-3 py-2 rounded-lg text-xs mb-3" style={{ backgroundColor: theme.colors.accentLight, color: theme.colors.accentPrimary }}>
            最佳方案：{overall.bestApproach}
          </div>
        )}
        {overall.keyAdjustments?.length > 0 && (
          <div>
            <span className="text-2xs font-semibold" style={{ color: "var(--color-text-muted)" }}>需重点调整：</span>
            {overall.keyAdjustments.map((a, i) => (
              <span key={i} className="inline-block text-2xs px-2 py-0.5 rounded mr-1 mt-1" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}>{a}</span>
            ))}
          </div>
        )}
      </div>

      {/* Production Difficulty */}
      {data.productionDifficulty && (
        <Section title="制作难度">
          <InfoRow label="场景复杂度" badge={<LevelBadge level={data.productionDifficulty.scenarioComplexity?.level} />}
            value={`${data.productionDifficulty.scenarioComplexity?.analysis || ""}${data.productionDifficulty.scenarioComplexity?.sceneCount ? ` (约${data.productionDifficulty.scenarioComplexity.sceneCount}个场景)` : ""}`} />
          <InfoRow label="特效需求" badge={<LevelBadge level={data.productionDifficulty.vfxRequirement?.level} />}
            value={data.productionDifficulty.vfxRequirement?.analysis} />
          <InfoRow label="演员需求" badge={<LevelBadge level={data.productionDifficulty.castRequirement?.level} />}
            value={`${data.productionDifficulty.castRequirement?.analysis || ""}${data.productionDifficulty.castRequirement?.mainRoles ? ` (${data.productionDifficulty.castRequirement.mainRoles}个主要角色)` : ""}`} />
          <InfoRow label="道具服装" badge={<LevelBadge level={data.productionDifficulty.propsAndCostume?.level} />}
            value={data.productionDifficulty.propsAndCostume?.analysis} />
        </Section>
      )}

      {/* Cost Estimate */}
      {data.costEstimate && (
        <Section title="成本评估">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-medium" style={{ color: "var(--color-text-secondary)" }}>预估规模：</span>
            <span className="text-xs px-2 py-0.5 rounded-lg font-semibold" style={{ backgroundColor: theme.colors.accentLight, color: theme.colors.accentPrimary }}>{data.costEstimate.scale}</span>
          </div>
          {data.costEstimate.mainCosts?.length > 0 && (
            <div className="mb-3">
              <span className="text-2xs font-semibold" style={{ color: "var(--color-text-muted)" }}>主要成本：</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {data.costEstimate.mainCosts.map((c, i) => (
                  <span key={i} className="text-2xs px-2 py-0.5 rounded" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}>{c}</span>
                ))}
              </div>
            </div>
          )}
          {data.costEstimate.costEfficiency && (
            <p className="text-2xs leading-relaxed" style={{ color: "var(--color-text-muted)" }}>{data.costEstimate.costEfficiency}</p>
          )}
        </Section>
      )}

      {/* Market Fit */}
      {data.marketFit && (
        <Section title="市场适配">
          <div className="grid grid-cols-3 gap-3 mb-3">
            {[
              { label: "播出平台", value: data.marketFit.platform },
              { label: "制作形式", value: data.marketFit.format },
              { label: "时长建议", value: data.marketFit.duration },
            ].map((item, i) => (
              <div key={i} className="rounded-lg p-3 text-center" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                <div className="text-2xs mb-1" style={{ color: "var(--color-text-muted)" }}>{item.label}</div>
                <div className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>{item.value || "-"}</div>
              </div>
            ))}
          </div>
          {data.marketFit.analysis && <p className="text-2xs leading-relaxed" style={{ color: "var(--color-text-muted)" }}>{data.marketFit.analysis}</p>}
        </Section>
      )}

      {/* Risk Assessment */}
      {data.riskAssessment && (
        <Section title="风险评估">
          <InfoRow label="审查风险" badge={<LevelBadge level={data.riskAssessment.censorshipRisk?.level} />} value={data.riskAssessment.censorshipRisk?.analysis} />
          <InfoRow label="市场风险" badge={<LevelBadge level={data.riskAssessment.marketRisk?.level} />} value={data.riskAssessment.marketRisk?.analysis} />
          <InfoRow label="制作风险" badge={<LevelBadge level={data.riskAssessment.productionRisk?.level} />} value={data.riskAssessment.productionRisk?.analysis} />
        </Section>
      )}
    </div>
  );
};

export default ShootabilityResult;
